/* Every word in the merchant dashboard.
 *
 * ITS OWN TABLE, NOT THE APPS' ONE. The Arabic sheet is scoped to the customer
 * app and the till (ruled 2026-09-17): a merchant reading a report in English is
 * an inconvenience, a customer who cannot read their own card is a failure. The
 * dashboard's Arabic comes after those two, so its strings sit here, outside
 * that sheet, and `scripts/copy-check.mjs` still reads this file for the house
 * rules: no em-dashes, no words ruled off every surface, and no string that
 * guesses somebody's gender.
 *
 * ENGLISH ONLY, AND SAYING SO. `ar` is null on every entry, which the check
 * counts as "no Arabic at all" rather than as unread Arabic. A missing string
 * falls back to English and is obvious; unread Arabic is obvious only to the
 * person reading it.
 *
 * WHAT IS NOT IN HERE is as deliberate as what is: no plan ladder, no invoice,
 * no storefront, no review, no campaign, no API key. See
 * reports/surfaces/merchant-dashboard.md for the reading that cut each one.
 */
export type Lang = "en" | "ar";

type Entry = { en: string; ar: string | null; review: null; source: null };

const S = (en: string): Entry => ({ en, ar: null, review: null, source: null });

export const COPY = {
  // --- the shell -----------------------------------------------------------
  "nav.overview": S("Overview"),
  "nav.counter": S("Counter"),
  "counter.back": S("Back to the dashboard"),
  "counter.endShift": S("End shift"),
  "nav.customers": S("Customers"),
  "nav.programme": S("Programme"),
  "nav.rewards": S("Rewards"),
  "nav.branches": S("Branches"),
  "nav.team": S("Team"),
  "nav.import": S("Bring members across"),
  "nav.card": S("Counter card"),
  "nav.reports": S("Exports"),
  "nav.settings": S("Settings"),
  "shell.signOut": S("Sign out"),
  "shell.menu": S("Menu"),
  "shell.menuClose": S("Close menu"),
  "shell.role.owner": S("Owner"),
  "shell.role.manager": S("Manager"),
  "shell.role.staff": S("Staff"),
  "shell.loading": S("Loading"),
  "shell.offline": S("No connection. Nothing here is up to date."),
  "shell.problem": S("Something went wrong reading your shop. Nothing was changed."),
  "shell.range.30": S("Last 30 days"),
  "shell.range.7": S("Last 7 days"),
  "shell.range.90": S("Last 90 days"),

  // --- sign in -------------------------------------------------------------
  // --- signing in (2026-09-18): the username and password Damga issued -------
  // No Google, Microsoft or emailed-code sign-in here any more: those are the
  // customer's doors. The code below is the second factor, after the password.
  "auth.title": S("Your shop, on Damga"),
  "auth.body": S("Sign in with the username Damga gave you and the password you chose."),
  "auth.username": S("Username"),
  "auth.password": S("Password"),
  "auth.show": S("Show"),
  "auth.hide": S("Hide"),
  "auth.submit": S("Sign in"),
  "auth.working": S("One moment"),
  "auth.refused": S("That username and password do not match, or this sign-in is paused for fifteen minutes after too many tries."),
  "auth.codeTitle": S("Check your email"),
  "auth.codeBody": S("This device is new to your sign-in, so we have emailed you six digits. They work once, for ten minutes."),
  "auth.codeLabel": S("The six digits"),
  "auth.verify": S("Verify and continue"),
  "auth.codeRefused": S("That code did not work. It may have expired or been used; start again for a new one."),
  "auth.codeIncomplete": S("Enter all six digits."),
  "auth.startAgain": S("Start again"),
  "auth.tooManyCodes": S("Five codes have gone out in the last hour. Use the newest one, or wait a little."),
  "auth.mailDown": S("We could not send the email just now. Nothing has changed; try again in a minute."),
  "auth.unreachable": S("Could not reach Damga. Check the connection and try again."),
  "auth.notStaff": S("This sign-in is not on a shop's team any more. Ask the shop's owner."),

  // --- choosing a password, from the emailed link ------------------------------
  "setup.checking": S("Opening your link"),
  "setup.title": S("Choose your password"),
  "setup.body": S("Your username is {username}. Choose a password only you know. Damga never sees it."),
  "setup.username": S("Username"),
  "setup.new": S("New password"),
  "setup.confirm": S("The same password again"),
  "setup.rule": S("At least 12 characters. A few ordinary words together is easier to remember than one strange one."),
  "setup.mismatch": S("The two passwords are not the same."),
  "setup.weak": S("The password needs to be {reason}."),
  "setup.submit": S("Set my password"),
  "setup.doneTitle": S("Your password is set"),
  "setup.doneBody": S("Sign in as {username}. The first time on each device we will email you six digits to check it is you."),
  "setup.signIn": S("Sign in"),
  "setup.deadTitle": S("This link has already been used, or has expired"),
  "setup.deadBody": S("A link works once, for three days. Ask your shop's owner for a new one, or Damga if you own the shop."),

  // --- overview ------------------------------------------------------------
  "overview.title": S("Overview"),
  "overview.stat.stamps": S("stamps"),
  "overview.stat.rewards": S("rewards taken"),
  "overview.stat.members": S("members"),
  "overview.stat.joined": S("joined"),
  "overview.tiers": S("Members by tier"),
  "overview.tiers.at": S("at {n}"),
  "overview.tiers.none": S("Your programme has no tiers yet."),
  "overview.activity": S("Latest activity"),
  "overview.activity.none": S("Nothing has happened at your counter yet."),
  "overview.activity.allCustomers": S("All customers"),
  "overview.empty.title": S("No stamps yet"),
  "overview.empty.body": S("When your counter stamps its first customer, it shows here."),

  /* BEFORE YOUR FIRST STAMP, and it is not a publish gate.
   *
   * The intake artboard draws five automatic checks in front of appearing in
   * Discover. There is no Discover listing, so there is nothing to be published
   * to and no gate to pass; and one of its five, a contact number a customer can
   * call, is not answerable at all because `businesses` has no such column.
   *
   * What is left is genuinely useful and is what this says instead: the four
   * things that have to be true before this shop can take a stamp, each one read
   * off a route the dashboard already loads. It is a list, not a gate: nothing
   * is withheld until it passes. */
  "ready.title": S("Before your first stamp"),
  "ready.body": S("Four things have to be true. Three are set up with us; the fourth is you opening the till."),
  "ready.done": S("Ready"),
  "ready.todo": S("Not yet"),
  "ready.programme": S("A card with a reward on it"),
  "ready.programme.yes": S("{reward} after {n} stamps."),
  "ready.programme.no": S("We set this up with you. It is five minutes on a call."),
  "ready.branch": S("A branch to stamp at"),
  "ready.branch.yes": S("{n} set up."),
  "ready.branch.no": S("Tell us where you are and we will add it."),
  "ready.staff": S("Somebody who can work the till"),
  "ready.staff.yes": S("{n} with a PIN."),
  "ready.staff.no": S("Your team needs a PIN each, so every stamp says who gave it."),
  "ready.till": S("A counter that has checked in"),
  "ready.till.yes": S("{n} seen."),
  "ready.till.no": S("Open counter.damga.qa on any tablet or phone and sign in. Nothing to install."),
  "ready.allSet": S(
    "Everything is ready. The next stamp your counter takes appears here.",
  ),
  "ready.notDiscover": S(
    "None of this is a gate. Nothing is held back until it passes, and nothing here lists your shop "
    + "anywhere: Damga has no directory for customers to browse yet.",
  ),

  // --- customers -----------------------------------------------------------
  "customers.title": S("Customers"),
  "customers.count": S("{n} on the card"),
  "customers.search": S("Search by name"),
  "customers.none.title": S("Nobody yet"),
  "customers.none.body": S("A customer joins the moment your counter stamps them."),
  "customers.noMatch": S("No customer matches that search."),
  "customers.col.name": S("Customer"),
  "customers.col.card": S("Card"),
  "customers.col.visits": S("Visits"),
  "customers.col.rewards": S("Rewards"),
  "customers.col.last": S("Last visit"),
  "customers.col.branch": S("Branch"),
  // A membership whose person has erased themselves: the shop's record stays,
  // the person is gone, and the table says which rather than showing a blank.
  "customers.erased": S("Erased account"),
  "customers.never": S("Never"),
  // The number is masked here as it is at the till. The dashboard is not a
  // place to collect phone numbers.
  "customers.masked": S("Shown as your counter shows it."),

  // --- programme -----------------------------------------------------------
  "programme.title": S("Programme"),
  "programme.none.title": S("No programme yet"),
  "programme.none.body": S("Set up your card: how many stamps a customer collects, and what they earn."),
  "programme.create.stamps": S("Stamps to fill the card"),
  "programme.create.reward": S("What they earn"),
  "programme.create.rewardHint": S("For example: a free coffee"),
  "programme.create.gap": S("Each customer can be stamped once every {n} minutes at most. If your shop needs a different gap, tell us before your first customer."),
  "programme.create.submit": S("Start the card"),
  "programme.create.note": S("You can have one card. Once customers are collecting on it, changing the rule is done with us, so nobody's card changes under them."),
  "programme.create.problem": S("That card could not be started: {reason}."),
  "programme.type": S("Type"),
  "programme.type.stamp": S("Stamp card"),
  "programme.type.points": S("Points"),
  "programme.threshold": S("The reward"),
  "programme.rule": S("{reward} at {n}"),
  "programme.gap": S("At most one stamp every {n} minutes"),
  "programme.gap.none": S("No minimum gap between stamps"),
  "programme.validity": S("A reward lasts {n} days once earned"),
  // CORRECTED 2026-09-18: "What the customer sees" titled a bar fixed at 40%.
  "programme.preview": S("A new customer's card"),
  "programme.change": S("Changing the card is a conversation, not a button: existing cards keep the rule they were started on. Tell us and we will change it with you."),

  // --- rewards -------------------------------------------------------------
  "rewards.title": S("Rewards"),
  "rewards.issued": S("Earned"),
  "rewards.taken": S("Taken"),
  "rewards.owed": S("Earned and not yet taken"),
  "rewards.none.title": S("No rewards yet"),
  "rewards.none.body": S("A reward appears here the moment a card completes."),
  "rewards.col.when": S("When"),
  "rewards.col.who": S("Customer"),
  "rewards.col.reward": S("Reward"),
  "rewards.col.state": S("State"),
  "rewards.col.branch": S("Branch"),
  "rewards.col.staff": S("Staff"),

  // --- branches ------------------------------------------------------------
  "branches.title": S("Branches"),
  "branches.none.title": S("One place, so far"),
  "branches.none.body": S("Branches are set up with you. Tell us when you open another."),
  "branches.primary": S("Main"),
  "branches.stamps": S("stamps"),
  "branches.members": S("members"),
  "branches.staff": S("staff"),

  // --- team ----------------------------------------------------------------
  "team.title": S("Team"),
  "team.none.title": S("Nobody added yet"),
  "team.none.body": S("Add the people who work your till. Each gets their own sign-in."),
  "team.col.name": S("Name"),
  "team.col.pin": S("Manager PIN"),
  "team.pin.set": S("Set"),
  "team.pin.none": S("Not set"),
  "team.pin.locked": S("Locked"),
  "team.invite": S("Each person gets their own sign-in and chooses their own password from a link we email them. You never see it, and neither do we. On the dashboard they see the counter and nothing else."),
  "team.col.signIn": S("Sign-in"),
  "team.status.waiting": S("Waiting for their password"),
  "team.status.ready": S("Ready"),
  "team.add.title": S("Add someone to the till"),
  "team.add.username": S("Their username"),
  "team.add.usernameHint": S("Lowercase letters, digits, dots or dashes. For example: sara.till"),
  "team.add.email": S("Their email"),
  "team.add.submit": S("Add and send their link"),
  "team.add.done": S("Added. We have emailed {who} a link to choose a password."),
  "team.add.already": S("Added. They already have a Damga sign-in, and it now opens your counter too."),
  "team.add.problem": S("Not added: {reason}."),
  "team.resend": S("Send a new link"),
  "team.resent": S("A new link is on its way to them. The one before it no longer works."),
  "team.remove": S("Remove"),
  "team.removeConfirm": S("Remove {who} from your counter? Their sign-in stops opening your shop at once."),
  "team.removed": S("Removed."),
  "team.mailDown": S("The email could not be sent just now. Try again in a minute."),
  "team.tooManyLinks": S("Not sent: three links have gone to them today. Another can go after {at}."),
  "team.tooManyLinksLater": S("Not sent: three links have gone to them today. Try again tomorrow."),

  // --- exports -------------------------------------------------------------
  "reports.title": S("Exports"),
  "reports.body": S("What is on these screens, as a file you can open in a spreadsheet."),
  "reports.customers": S("Customers"),
  "reports.rewards": S("Rewards"),
  "reports.download": S("Download CSV"),
  "reports.note": S("Made now, from your own shop's records. Nothing is emailed and nothing is stored."),

  // --- settings ------------------------------------------------------------
  "settings.title": S("Settings"),
  "settings.shop": S("Your shop"),
  "settings.name": S("Name"),
  "settings.role": S("Your role"),
  /* 048. The number the shop answers, which Damga never had on file until
   * now: enrolment took the owner's number to find their account and threw it
   * away. Shown so the shop can see what we would call, and so a wrong or
   * missing one is noticed by the person who knows the right one. */
  "settings.number": S("The number we call you on"),
  "settings.number.none": S("None on file. Tell us the number your shop answers, so we can reach you."),
  "settings.city": S("City"),
  "settings.plan": S("What you pay"),
  // The ruled pricing, the same words as the public page. The artboard's four
  // plans and its Change plan control are not built: they contradict this.
  "settings.plan.pilot": S("Pilot: no cost for the first 90 days."),
  "settings.plan.after": S("After that, QAR 299 per location per month. One flat price, no per-stamp fee, every feature."),
  "settings.plan.nothing": S("There is nothing to cancel: the pilot simply ends."),
  "settings.data": S("Your shop's records"),
  "settings.data.body": S("Every stamp and reward at your counter is yours. Ask us and we send the lot as a file."),
  "settings.close": S("Leaving Damga"),
  "settings.close.body": S("Tell us and we start the wind-down: your counter stops taking stamps, your customers keep the rewards they have earned for sixty days, and nothing is deleted."),
  "settings.contact": S("Talk to us"),
  "settings.contact.body": S("Damga is a small team in Doha. Whoever set your shop up is who you reach."),

  /* The card that goes on the counter.
   *
   * WHAT IT DOES NOT SAY IS THE CAREFUL PART. It does not mention an app, and it
   * does not tell anybody to download anything: a customer enrolled at a till
   * cannot reach their card in the app while SMS is parked (DECISIONS,
   * 2026-09-16), so "see your stamps in the app" would be false on the one
   * surface a customer reads without us there. What it says instead is true
   * today: ask us, and ask us again whenever you want to know where you are.
   */
  "card.title": S("Counter card"),
  "card.body": S(
    "The card that sits by your till, and a sticker for the window. Print both on A5 paper; "
    + "the sticker has a line to cut along.",
  ),
  "card.print": S("Print both"),
  "card.what": S("What is on it"),
  "card.what.body": S(
    "Your shop's name, what the reward is, and how somebody joins. Nothing else, because "
    + "a card by a till is read in about two seconds.",
  ),
  // CORRECTED 2026-09-18 (the end-to-end test): since the email at the till, some
  // customers added there can open their card in the app; not all, and none in
  // production until the email line is approved. True in both, and still a reason
  // the printed card promises nothing about an app.
  "card.noApp": S(
    "It does not mention an app. A customer added at your counter can open their card in one "
    + "only if the till kept their email, and not everyone gives one, so the card promises it to nobody.",
  ),
  "card.english": S(
    "English only for now. The Arabic follows the customer app and the counter, which are "
    + "waiting on a native speaker to read them, because nothing in Arabic ships until "
    + "somebody has.",
  ),
  "card.noProgramme.title": S("Your card has no reward yet"),
  "card.noProgramme.body": S(
    "The counter card says what somebody gets and after how many stamps. Once your programme "
    + "is set up, this prints.",
  ),

  /* The words that are actually on the printed card. Short, because that is
   * the whole design constraint. */
  "card.p.collect": S("Collect stamps here"),
  "card.p.reward": S("{reward} after {n} stamps"),
  "card.p.join": S("Ask us to add you"),
  "card.p.how": S("We save your stamps to your mobile number, so there is nothing to carry and nothing to lose."),
  "card.p.check": S("Ask at the counter any time to hear how many you have."),
  "card.p.mark": S("On Damga"),
  "card.p.cut": S("Cut along this line"),

  /* Bringing paper cards across. The one place this dashboard writes anything,
   * and the reasoning is in App.tsx: an import is a shop declaring the
   * customers it already has, not an edit to a card somebody is holding. */
  "import.title": S("Bring members across"),
  "import.body": S(
    "If your regulars already have a paper card, type what they hold and Damga will carry it over. "
    + "Nothing is written until you have seen what it will do.",
  ),
  "import.phone": S("Mobile number"),
  "import.phonePh": S("+974 ····"),
  "import.stamps": S("Stamps on the card"),
  "import.name": S("Name"),
  "import.nameOptional": S("Optional"),
  "import.add": S("Add this member"),
  "import.addAnother": S("Add another"),
  "import.rows": S("{n} to bring across"),
  "import.remove": S("Remove"),
  "import.check": S("Check what this will do"),
  "import.checking": S("Checking"),
  "import.startOver": S("Start again"),
  "import.badPhone": S("That does not look like a mobile number. It needs the country code."),
  "import.badStamps": S("Stamps has to be a whole number, and not more than 1000."),
  "import.duplicate": S("That number is already on the list below."),
  "import.empty.title": S("Nobody on the list yet"),
  "import.empty.body": S(
    "Add one member at a time: a number and how many stamps they hold. About twelve seconds each, "
    + "and it can be done between customers.",
  ),

  /* The plan, which is the whole point of the two steps. */
  "import.plan.title": S("What this will do"),
  "import.plan.members": S("Members"),
  "import.plan.merged": S("Same number twice"),
  "import.plan.capped": S("More than your card holds"),
  "import.plan.ladder": S("Your card completes at"),
  "import.plan.mergedBody": S(
    "Where the same number appears twice we keep the higher count. A regular who loses stamps in a "
    + "move tells six people.",
  ),
  "import.plan.cappedBody": S(
    "A count above what your card holds is set to the top and the reward is claimable straight away. "
    + "The same rule, and it favours the customer for the same reason.",
  ),
  "import.plan.cappedRow": S("{phone} said {declared}, set to {capped}"),
  "import.apply": S("Bring these members across"),
  "import.applying": S("Bringing them across"),

  /* The review gate, which is the product being careful rather than slow. */
  "import.review.title": S("A person at Damga will look at this first"),
  "import.review.body": S(
    "A large move, or one where most cards are nearly complete, is read by somebody here before it "
    + "lands. It is saved and nothing is lost. We will come back to you.",
  ),

  "import.done.title": S("Brought across"),
  "import.done.body": S(
    "{n} members now hold on Damga what they held on paper. Each one shows on their Activity as an "
    + "opening balance, so nobody is left wondering where the stamps came from.",
  ),
  "import.problem": S("Something went wrong and nothing was brought across. Try again."),
  "import.spreadsheet": S(
    "Have it in a spreadsheet already? Send it to us and we will load it, rather than have you type "
    + "it twice.",
  ),
} as const;

export type CopyKey = keyof typeof COPY;

export function t(key: CopyKey, params: Record<string, string | number> = {}): string {
  return COPY[key].en.replace(/\{(\w+)\}/g, (whole, name) =>
    name in params ? String(params[name]) : whole,
  );
}

/** Every string here is the author's; none has been read by the founder. */
export const PENDING_APPROVAL: readonly { prefix?: string; keys?: readonly string[]; because: string }[] = [
  {
    prefix: "",
    because:
      "The merchant dashboard, built 2026-09-17. Every line is written here rather "
      + "than mined from the artboard, because the artboard's dashboard describes "
      + "plans, a storefront, reviews and campaigns that do not exist.",
  },
];

export function pendingApproval(): CopyKey[] {
  return Object.keys(COPY) as CopyKey[];
}

/** All of them: this surface ships English until the other two have Arabic. */
export function missingArabic(): CopyKey[] {
  return Object.keys(COPY) as CopyKey[];
}
